import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Download, FileText, Loader2, QrCode, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

import PortalLayout from '../PortalLayout';
import {
  usePortalSummary,
  usePortalPaperwork,
  fetchPortalQrObjectUrl,
  openPortalSignedPdf,
} from '../../../services/portal';
import { TILE, TILE_PAD, StatTile, ErrorState, fmtDate } from '../portalShared';

// My Link — the partner's QR code (view + download), copyable links, scan/visit
// mini-stats, and their signed paperwork.
const PortalLink = () => {
  const summary = usePortalSummary();
  const paperwork = usePortalPaperwork();
  const [qrUrl, setQrUrl] = useState(null);
  const [qrError, setQrError] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const cancelledRef = useRef(false);

  const loadQr = useCallback(async () => {
    setQrError(false);
    try {
      const objectUrl = await fetchPortalQrObjectUrl();
      if (cancelledRef.current) {
        // Unmounted mid-fetch — revoke immediately or the blob URL leaks.
        URL.revokeObjectURL(objectUrl);
        return;
      }
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    } catch (e) {
      if (!cancelledRef.current) setQrError(true);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    loadQr();
    return () => {
      cancelledRef.current = true;
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [loadQr]);

  const s = summary.data;

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied!`);
    } catch (e) {
      toast.error('Could not copy — please copy it manually.');
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = 'veritas-referral-qr.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const viewDoc = async (envelopeId) => {
    if (viewingId) return;
    setViewingId(envelopeId);
    try {
      await openPortalSignedPdf(envelopeId);
    } catch (e) {
      toast.error('Could not open the document.');
    } finally {
      setViewingId(null);
    }
  };

  const docs = paperwork.data?.documents || [];

  return (
    <PortalLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-white">My link &amp; QR code</h1>
        <p className="mt-0.5 text-sm text-neutral-400">
          Share either one — every signup through them is credited to you automatically.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* QR card */}
        <div className={`${TILE_PAD} flex flex-col items-center`}>
          {qrError ? (
            <div className="flex h-56 w-56 flex-col items-center justify-center gap-3 rounded-lg bg-white/5 px-4 text-center text-sm text-neutral-300">
              Couldn&apos;t load your QR code.
              <button
                type="button"
                onClick={loadQr}
                className="min-h-[44px] rounded-md bg-white/10 px-5 text-sm font-medium text-white transition-colors hover:bg-white/15"
              >
                Retry
              </button>
            </div>
          ) : qrUrl ? (
            <img
              src={qrUrl}
              alt="Your referral QR code"
              width={224}
              height={224}
              className="rounded-lg bg-white p-3"
            />
          ) : (
            <div className="flex h-56 w-56 animate-pulse items-center justify-center rounded-lg bg-white/10">
              <QrCode className="h-8 w-8 text-neutral-500" />
            </div>
          )}
          <button
            type="button"
            onClick={downloadQr}
            disabled={!qrUrl}
            className="mt-4 inline-flex min-h-[48px] w-full max-w-[224px] items-center justify-center gap-2 rounded-md bg-black text-sm font-semibold text-white transition-colors hover:!bg-white hover:!text-black disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Download PNG
          </button>
          <p className="mt-3 text-center text-xs leading-snug text-neutral-400">
            Print it, share it, post it — scans are counted and always point to your current link.
          </p>
        </div>

        {/* Links + mini stats */}
        <div className="flex flex-col gap-4">
          <div className={TILE_PAD}>
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
              Referral link
            </div>
            <div className="mt-1 break-all text-sm text-white">{s?.link?.url || '—'}</div>
            <button
              type="button"
              onClick={() => copy(s?.link?.url, 'Link')}
              disabled={!s?.link?.url}
              className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-white/10 px-4 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:opacity-40"
            >
              <Copy className="h-4 w-4" /> Copy link
            </button>
            {s?.link?.shortUrl ? (
              <>
                <div className="mt-4 text-xs font-medium uppercase tracking-wide text-neutral-400">
                  Short link (what the QR encodes)
                </div>
                <div className="mt-1 break-all text-sm text-white">{s.link.shortUrl}</div>
                <button
                  type="button"
                  onClick={() => copy(s.link.shortUrl, 'Short link')}
                  className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-white/10 px-4 text-sm font-medium text-white transition-colors hover:bg-white/15"
                >
                  <Copy className="h-4 w-4" /> Copy short link
                </button>
              </>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={QrCode} label="QR scans" value={s?.stats?.scans ?? 0} loading={summary.isLoading} />
            <StatTile icon={Eye} label="Link visits" value={s?.stats?.visits ?? 0} loading={summary.isLoading} />
          </div>
        </div>
      </div>

      {/* Paperwork */}
      <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-neutral-300">
        Your signed paperwork
      </h2>
      <div className={TILE}>
        {paperwork.isLoading ? (
          <div className="space-y-3 p-4">
            <div className="h-4 w-48 animate-pulse rounded-md bg-white/10" />
            <div className="h-4 w-40 animate-pulse rounded-md bg-white/10" />
          </div>
        ) : paperwork.isError ? (
          <div className="p-3">
            <ErrorState onRetry={paperwork.refetch} />
          </div>
        ) : docs.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-neutral-400">
            No documents on file.
          </div>
        ) : (
          docs.map((doc) => (
            <button
              key={doc.envelopeId}
              type="button"
              onClick={() => viewDoc(doc.envelopeId)}
              disabled={Boolean(viewingId)}
              className="flex min-h-[52px] w-full items-center gap-3 border-b border-white/5 px-5 text-left text-sm text-white transition-colors last:border-0 hover:bg-white/5 disabled:opacity-60"
            >
              <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
              <span className="min-w-0 flex-1 truncate">{doc.name}</span>
              {viewingId === doc.envelopeId ? (
                <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
              ) : (
                <span className="text-xs text-neutral-400">
                  {doc.completedAt ? fmtDate(doc.completedAt) : ''}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalLink;
